import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const Dashboard = () => {
    const { currentUser, userData, isAdmin } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalMembers: 0,
        upcomingEvents: 0,
        activeMissions: 0,
        activeProjects: 0,
        financialBalance: 0
    });

    const bibleVerses = [
        { verse: "For I know the plans I have for you, declares the LORD, plans for welfare and not for evil, to give you a future and a hope.", reference: "Jeremiah 29:11" },
        { verse: "I can do all things through him who strengthens me.", reference: "Philippians 4:13" },
        { verse: "The LORD is my strength and my shield; in him my heart trusts, and I am helped.", reference: "Psalm 28:7" },
        { verse: "Trust in the LORD with all your heart, and do not lean on your own understanding.", reference: "Proverbs 3:5" },
        { verse: "Be strong and courageous. Do not fear or be in dread of them, for it is the LORD your God who goes with you.", reference: "Deuteronomy 31:6" }
    ];

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        setLoading(true);
        try {
            // Load total members
            const membersQuery = query(collection(db, 'users'), where('status', '==', 'active'));
            const membersSnapshot = await getDocs(membersQuery);
            
            // Load upcoming events
            const thirtyDaysFromNow = new Date();
            thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
            const eventsQuery = query(
                collection(db, 'events'),
                where('startDate', '>=', new Date()),
                where('startDate', '<=', thirtyDaysFromNow)
            );
            const eventsSnapshot = await getDocs(eventsQuery);
            
            // Load active missions
            const missionsQuery = query(collection(db, 'missions'), where('status', '==', 'active'));
            const missionsSnapshot = await getDocs(missionsQuery);
            
            // Load active projects
            const projectsQuery = query(collection(db, 'projects'), where('status', '==', 'active'));
            const projectsSnapshot = await getDocs(projectsQuery);
            
            let balance = 0;
            if (isAdmin) {
                const financesQuery = query(collection(db, 'finances'), orderBy('date', 'desc'), limit(1));
                const financesSnapshot = await getDocs(financesQuery);
                if (!financesSnapshot.empty) {
                    balance = financesSnapshot.docs[0].data().balance || 0;
                }
            }

            setStats({
                totalMembers: membersSnapshot.size,
                upcomingEvents: eventsSnapshot.size,
                activeMissions: missionsSnapshot.size,
                activeProjects: projectsSnapshot.size,
                financialBalance: balance
            });
        } catch (error) {
            console.error('Error loading stats:', error);
            showToast('Error loading statistics', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    const getMotivationalMessage = () => {
        const hour = new Date().getHours();
        if (hour < 12) {
            return "May this morning bring you renewed strength and divine blessings as you serve in God's vineyard.";
        } else if (hour < 18) {
            return "May your afternoon be filled with God's grace and opportunities to shine His light.";
        } else {
            return "As the day winds down, may you find peace and rest in God's loving presence.";
        }
    };

    const getRandomVerse = () => {
        return bibleVerses[Math.floor(Math.random() * bibleVerses.length)];
    };

    const StatCard = ({ title, value, description, link, adminOnly = false }) => {
        if (adminOnly && !isAdmin) return null;
        
        return (
            <div className="dashboard-stat-card" onClick={() => navigate(link)}>
                <h3>{title}</h3>
                <div className="dashboard-stat-value">
                    {loading ? <span className="dashboard-loading"></span> : value}
                </div>
                <p className="dashboard-stat-description">{description}</p>
            </div>
        );
    };

    const ActionCard = ({ icon, title, description, link }) => (
        <div className="dashboard-action-card" onClick={() => navigate(link)}>
            <i className={`fas ${icon}`}></i>
            <h3>{title}</h3>
            <p>{description}</p>
        </div>
    );

    const AdminActionCard = ({ icon, title, description, onClick }) => (
        <div className="dashboard-admin-action-card" onClick={onClick}>
            <i className={`fas ${icon}`}></i>
            <h3>{title}</h3>
            <p>{description}</p>
        </div>
    );

    // Get first name from userData
    const firstName = userData?.fullName?.split(' ')[0] || userData?.firstName || 'Beloved';
    const randomVerse = getRandomVerse();

    return (
        <div className="dashboard-page">
            {/* Welcome Section */}
            <section className="dashboard-welcome-section">
                <h1>{getGreeting()}, {firstName}!</h1>
                <p>{getMotivationalMessage()}</p>
                <p className="dashboard-scripture-verse">
                    "{randomVerse.verse}" - {randomVerse.reference}
                </p>
            </section>

            {/* Stats Overview */}
            <div className="dashboard-stats-grid">
                <StatCard
                    title="Total Members"
                    value={stats.totalMembers}
                    description="Active members in the ministry"
                    link="/members"
                />
                <StatCard
                    title="Upcoming Events"
                    value={stats.upcomingEvents}
                    description="Events scheduled this month"
                    link="/events"
                />
                <StatCard
                    title="Active Missions"
                    value={stats.activeMissions}
                    description="Ongoing outreach programs"
                    link="/missions"
                />
                <StatCard
                    title="Active Projects"
                    value={stats.activeProjects}
                    description="Current ministry projects"
                    link="/projects"
                />
                <StatCard
                    title="Financial Balance"
                    value={`Ksh ${stats.financialBalance.toLocaleString()}`}
                    description="Current ministry funds"
                    link="/finance"
                    adminOnly={true}
                />
            </div>

            {/* Admin Panel */}
            {isAdmin && (
                <div className="dashboard-admin-panel">
                    <h2><i className="fas fa-user-shield"></i> Admin Panel</h2>
                    <div className="dashboard-admin-actions">
                        <AdminActionCard
                            icon="fa-calendar-plus"
                            title="Add New Year"
                            description="Create a new ministry year with settings"
                            onClick={() => window.dispatchEvent(new CustomEvent('openModal', { detail: { modalId: 'addYearModal' } }))}
                        />
                        <AdminActionCard
                            icon="fa-bell"
                            title="Send Notification"
                            description="Send push notifications to all members"
                            onClick={() => window.dispatchEvent(new CustomEvent('openModal', { detail: { modalId: 'notificationModal' } }))}
                        />
                        <AdminActionCard
                            icon="fa-user-shield"
                            title="Manage Admins"
                            description="Add or remove administrator access"
                            onClick={() => showToast('This feature is under development', 'info')}
                        />
                        <AdminActionCard
                            icon="fa-chart-bar"
                            title="View Reports"
                            description="Generate ministry activity reports"
                            onClick={() => navigate('/reports')}
                        />
                         <AdminActionCard
                            icon="fas fa-envelope"
                            title="Email communication"
                            description="Communicate to members via email"
                            onClick={() => navigate('/communication')}
                        />
                        <AdminActionCard
                            icon="fas fa-comment"
                            title="Sms communication"
                            description="Communicate to members via text sms"
                            onClick={() => navigate('/SMScommunication')}
                        />
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            <h2 className="dashboard-quick-actions-title">Quick Actions</h2>
            <div className="dashboard-quick-actions">
                <ActionCard
                    icon="fa-user-plus"
                    title="Register Member"
                    description="Add new members to the ministry"
                    link="/register"
                />
                <ActionCard
                    icon="fa-calendar-plus"
                    title="Create Event"
                    description="Schedule a new ministry event"
                    link="/events"
                />
                 <ActionCard
                    icon="fa-praying-hands"
                    title="MORNING PRAYER-FLAME"
                    description="Pray with us.Present your prayer Request and join us everyday 5:30am to 6am"
                    link="/prayers"
                />
                 <ActionCard
                    icon="fa-solid fa-bible"
                    title="MISSIONS TOOLKIT"
                    description="Mission groups and  records"
                    link="/missiontoolkit"
                />
                <ActionCard
                    icon="fa-hand-holding-heart"
                    title="Help me"
                    description="I need the ministry's intervention"
                    link="/help-me"
                />
                <ActionCard
                    icon="fa-gear"
                    title="Update Profile"
                    description="Maintain your profile information"
                    link="/settings"
                />
                <ActionCard
                    icon="fa-hands-helping"
                    title="Start/Support Mission"
                    description="Launch a new outreach program"
                    link="/missions"
                />
            </div>
        </div>
    );
};

export default Dashboard;
